import { useEffect, useState } from 'react';
import { useBrandLocale } from '../../hooks/useBrandLocale';
import { toast } from 'react-toastify';
import { ServiceSiteKind } from '../../../__generated__/graphql';
import { AddressInput } from '../../components/addressInput';
import { Button } from '../../components/button';
import { Modal } from '../../components/modal';
import {
  Alert,
  Field,
  FormGrid,
  Input,
  InlineRow,
  LoadingBlock,
  Muted,
  Select,
  Stack,
  Strong,
  Tabs,
  Textarea,
} from '../../components/ui';
import { MY_SERVICE_SITES } from '../../services/serviceService/queries';
import {
  useCreateServiceSiteMutation,
  useServiceSiteAccessInstructionsQuery,
  useUpdateServiceSiteMutation,
} from '../../services/serviceService/__generated__/queries.generated';
import { CheckRow } from '../ServiceProviderPage/serviceControls';
import { addressFromPlace } from '../StayHostPage/stayAddress';
import { firstGraphQLError, stayErrorMessage } from '../StayHostPage/stayErrors';
import { CalendarSyncSection } from './CalendarSyncSection';
import {
  CALENDAR_FIELD_OF,
  emptySiteForm,
  siteToForm,
  toSiteInput,
  VEHICLE_CLASS_OPTIONS,
  type ServiceSite,
  type SiteForm,
  type SiteFormErrors,
} from './serviceSiteForm';

/** Server field problems (`details.errors[].field`, or `details.field` for the link). */
function serverSiteErrors(error: unknown): SiteFormErrors {
  const out: SiteFormErrors = {};
  const ext = firstGraphQLError(error)?.extensions as any;
  if (ext?.code === 'CALENDAR_URL_INVALID') out.calendarUrl = firstGraphQLError(error)?.message;
  for (const item of Array.isArray(ext?.details?.errors) ? ext.details.errors : []) {
    const field = CALENDAR_FIELD_OF[String(item?.field ?? item?.path ?? '')];
    if (field && !out[field]) out[field] = String(item?.message ?? 'Check this');
  }
  return out;
}

/**
 * Add or change a home or a vehicle (spec 019 W14). The address comes from
 * Places with its map point; a home has its size, supplies (D22) and how to
 * get in; a vehicle its make, model and size. The door code is shown only to
 * its owner and reaches a worker shortly before the job (D7).
 */
export function SiteFormModal({
  site,
  kind = ServiceSiteKind.Home,
  initial,
  onClose,
  onSaved,
}: {
  site?: ServiceSite | null;
  kind?: ServiceSiteKind;
  /** Prefilled fields for a new site, e.g. `linkedCarId` from a car trip (W27). */
  initial?: Partial<SiteForm>;
  onClose: () => void;
  onSaved?: (site: ServiceSite) => void;
}) {
  // Spec 028: a new home's area is in the brand's unit.
  const { areaUnit: brandAreaUnit } = useBrandLocale();
  const [form, setForm] = useState<SiteForm>(() =>
    site
      ? siteToForm(site, undefined, brandAreaUnit)
      : { ...emptySiteForm(kind, brandAreaUnit), ...initial },
  );
  useEffect(() => {
    // The brand answer can arrive after the form opened: a home with no area follows it.
    setForm(previous =>
      previous.sqft.trim() || previous.areaUnit === brandAreaUnit
        ? previous
        : { ...previous, areaUnit: brandAreaUnit },
    );
  }, [brandAreaUnit]);
  const [errors, setErrors] = useState<SiteFormErrors>({});
  const accessQuery = useServiceSiteAccessInstructionsQuery({
    variables: { siteId: site?.id ?? '' },
    skip: !site?.hasAccessInstructions,
    fetchPolicy: 'network-only',
  });
  const refetchQueries = [
    { query: MY_SERVICE_SITES },
    { query: MY_SERVICE_SITES, variables: { includeInactive: true } },
  ];
  const [create, { loading: creating }] = useCreateServiceSiteMutation({ refetchQueries });
  const [update, { loading: updating }] = useUpdateServiceSiteMutation({ refetchQueries });

  useEffect(() => {
    const text = accessQuery.data?.serviceSiteAccessInstructions;
    if (text != null) setForm(f => (f.accessChanged ? f : { ...f, accessInstructions: text }));
  }, [accessQuery.data]);

  const set = (patch: Partial<SiteForm>) => {
    setForm(f => ({ ...f, ...patch }));
    setErrors({});
  };

  const save = async () => {
    const { input, errors: found } = toSiteInput(form);
    if (Object.keys(found).length) {
      setErrors(found);
      return;
    }
    try {
      const result = site
        ? (await update({ variables: { siteId: site.id, input } })).data?.updateServiceSite
        : (await create({ variables: { input } })).data?.createServiceSite;
      if (result) {
        toast.success(site ? 'Saved' : `${result.nickname} added`);
        onSaved?.(result);
      }
      onClose();
    } catch (e) {
      setErrors({
        ...serverSiteErrors(e),
        form: stayErrorMessage(e, 'Could not save. Try again.'),
      });
    }
  };

  const isHome = form.kind === ServiceSiteKind.Home;

  return (
    <Modal
      isOpen
      onClose={onClose}
      title={site ? 'Edit' : 'Add a home or vehicle'}
      modalWidth="min(680px, calc(100vw - 2rem))"
    >
      <Stack $gap={4}>
        <Strong>{site ? `Edit ${site.nickname}` : 'Add a home or vehicle'}</Strong>
        {!site && (
          <Tabs
            items={[
              { key: ServiceSiteKind.Home, label: 'Home' },
              { key: ServiceSiteKind.Vehicle, label: 'Vehicle' },
            ]}
            value={form.kind}
            onChange={k => set({ kind: k })}
            aria-label="Kind"
          />
        )}
        {errors.form && (
          <Alert tone="error" title="Not saved">
            {errors.form}
          </Alert>
        )}
        <Field label="Name" htmlFor="site-name" required error={errors.nickname}>
          <Input
            id="site-name"
            value={form.nickname}
            maxLength={60}
            placeholder={isHome ? 'Beach house' : 'Blue RAV4'}
            onChange={e => set({ nickname: e.target.value })}
            data-testid="site-name"
          />
        </Field>
        <Field
          label={isHome ? 'Address' : 'Where the vehicle is parked'}
          error={errors.address}
          hint="Search, then check the street, city, state and ZIP below."
        >
          <AddressInput
            value=""
            placeholder="Search for the address"
            onLocationSelect={place => set({ address: addressFromPlace(place, form.address) })}
          />
        </Field>
        <FormGrid $cols={2}>
          <Field label="Street" htmlFor="site-street" required>
            <Input
              id="site-street"
              value={form.address.street}
              onChange={e => set({ address: { ...form.address, street: e.target.value } })}
              data-testid="site-street"
            />
          </Field>
          <Field label="Apt or unit" htmlFor="site-unit">
            <Input
              id="site-unit"
              value={form.address.unit}
              onChange={e => set({ address: { ...form.address, unit: e.target.value } })}
            />
          </Field>
          <Field label="City" htmlFor="site-city" required>
            <Input
              id="site-city"
              value={form.address.city}
              onChange={e => set({ address: { ...form.address, city: e.target.value } })}
              data-testid="site-city"
            />
          </Field>
          <FormGrid $cols={2}>
            <Field label="State" htmlFor="site-state" required>
              <Input
                id="site-state"
                maxLength={2}
                value={form.address.state}
                onChange={e => set({ address: { ...form.address, state: e.target.value } })}
                data-testid="site-state"
              />
            </Field>
            <Field label="ZIP" htmlFor="site-zip" required>
              <Input
                id="site-zip"
                value={form.address.zipCode}
                onChange={e => set({ address: { ...form.address, zipCode: e.target.value } })}
                data-testid="site-zip"
              />
            </Field>
          </FormGrid>
        </FormGrid>
        {isHome ? (
          <>
            <FormGrid $cols={3}>
              <Field
                label="Bedrooms"
                htmlFor="site-bd"
                required
                error={errors.bedrooms}
                hint="0 for a studio"
              >
                <Input
                  id="site-bd"
                  inputMode="numeric"
                  value={form.bedrooms}
                  onChange={e => set({ bedrooms: e.target.value })}
                />
              </Field>
              <Field label="Bathrooms" htmlFor="site-ba" required error={errors.bathrooms}>
                <Input
                  id="site-ba"
                  inputMode="decimal"
                  value={form.bathrooms}
                  onChange={e => set({ bathrooms: e.target.value })}
                />
              </Field>
              <Field
                label={form.areaUnit === 'sqm' ? 'Square metres' : 'Square feet'}
                htmlFor="site-sqft"
                error={errors.sqft}
              >
                <Input
                  id="site-sqft"
                  inputMode="numeric"
                  value={form.sqft}
                  onChange={e => set({ sqft: e.target.value })}
                />
              </Field>
              <Field label="Floors" htmlFor="site-floors" error={errors.floors}>
                <Input
                  id="site-floors"
                  inputMode="numeric"
                  value={form.floors}
                  onChange={e => set({ floors: e.target.value })}
                />
              </Field>
            </FormGrid>
            <CheckRow
              label="Pets live here or stay here"
              checked={form.pets}
              onChange={pets => set({ pets })}
            />
            <Strong>What the home has</Strong>
            <Muted $size="sm">
              We compare this with what the worker brings and tell you about gaps before you book.
            </Muted>
            <CheckRow label="Vacuum" checked={form.vacuum} onChange={vacuum => set({ vacuum })} />
            <CheckRow label="Washer" checked={form.washer} onChange={washer => set({ washer })} />
            <CheckRow label="Dryer" checked={form.dryer} onChange={dryer => set({ dryer })} />
            <FormGrid $cols={3}>
              <Field label="Spare linen sets" htmlFor="site-linens" error={errors.linenSets}>
                <Input
                  id="site-linens"
                  inputMode="numeric"
                  value={form.linenSets}
                  onChange={e => set({ linenSets: e.target.value })}
                />
              </Field>
              <Field
                label="Where the linens are"
                htmlFor="site-linens-at"
                error={errors.linensLocation}
              >
                <Input
                  id="site-linens-at"
                  value={form.linensLocation}
                  onChange={e => set({ linensLocation: e.target.value })}
                />
              </Field>
              <Field
                label="Where guest supplies are"
                htmlFor="site-consumables"
                error={errors.consumablesLocation}
              >
                <Input
                  id="site-consumables"
                  value={form.consumablesLocation}
                  onChange={e => set({ consumablesLocation: e.target.value })}
                />
              </Field>
            </FormGrid>
          </>
        ) : (
          <FormGrid $cols={3}>
            <Field label="Make" htmlFor="site-make" error={errors.make}>
              <Input
                id="site-make"
                value={form.make}
                onChange={e => set({ make: e.target.value })}
                data-testid="site-make"
              />
            </Field>
            <Field label="Model" htmlFor="site-model">
              <Input
                id="site-model"
                value={form.model}
                onChange={e => set({ model: e.target.value })}
              />
            </Field>
            <Field label="Year" htmlFor="site-year" error={errors.year}>
              <Input
                id="site-year"
                inputMode="numeric"
                value={form.year}
                onChange={e => set({ year: e.target.value })}
              />
            </Field>
            <Field label="Color" htmlFor="site-color">
              <Input
                id="site-color"
                value={form.color}
                onChange={e => set({ color: e.target.value })}
              />
            </Field>
            <Field
              label="Plate"
              htmlFor="site-plate"
              error={errors.plate}
              hint="Shown to the worker only for the job"
            >
              <Input
                id="site-plate"
                value={form.plate}
                maxLength={12}
                onChange={e => set({ plate: e.target.value })}
              />
            </Field>
            <Field
              label="Size"
              htmlFor="site-class"
              hint="We guess it from the make and model; change it if wrong"
            >
              <Select
                id="site-class"
                value={form.vehicleClass}
                onChange={e => set({ vehicleClass: e.target.value })}
              >
                {VEHICLE_CLASS_OPTIONS.map(o => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </Select>
            </Field>
            {form.linkedCarId && (
              <Field label="Linked listing">
                <Muted $size="sm">Your car {form.linkedCarId.slice(0, 8)} on the car site</Muted>
              </Field>
            )}
          </FormGrid>
        )}
        <Field
          label="How to get in"
          htmlFor="site-access"
          error={errors.accessInstructions}
          hint="A door code, a lockbox or where the key is. Workers see it 2 hours before the job."
        >
          {accessQuery.loading ? (
            <LoadingBlock label="Loading" minHeight="80px" />
          ) : (
            <Textarea
              id="site-access"
              value={form.accessInstructions}
              maxLength={2000}
              onChange={e => set({ accessInstructions: e.target.value, accessChanged: true })}
            />
          )}
        </Field>
        {isHome && <CalendarSyncSection site={site} form={form} errors={errors} set={set} />}
        <InlineRow>
          <Button
            type="button"
            text="Save"
            onClick={save}
            isLoading={creating || updating}
            dataTestId="site-save"
          />
          <Button type="button" theme="text" text="Cancel" onClick={onClose} />
        </InlineRow>
      </Stack>
    </Modal>
  );
}
